// External
import join from 'url-join';

// Internal
import { http } from '../../util/index';
import { asyncAction } from '../../util/actions';
import { handleError } from '../util';
import { flatten } from '../../util/array/flatten';

// Constants
const api2 = join(import.meta.env.VITE_API2_URL, 'v1');

/**
 * @name fetchCatalogTypes
 * @async
 * @desc fetches all existing catalogTypes
 */
export const fetchCatalogTypes = asyncAction({
  baseName: 'FETCH_CATALOG_TYPES',
  action: action => () => dispatch => {
    dispatch(action.start());

    return http
      .get(join(api2, '/catalogTypes?count=10000'))
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => handleError(dispatch, action, error));
  }
});

/**
 * @name fetchCatalogTypeById
 * @async
 * @desc fetches a catalogType by ID
 * @param {string} catalogTypeId the catalogType's ID
 */
export const fetchCatalogTypeById = asyncAction({
  baseName: 'FETCH_CATALOG_TYPE_BY_ID',
  action: action => catalogTypeId => dispatch => {
    dispatch(action.start());

    return http
      .get(join(api2, `/catalogTypes/${catalogTypeId}`))
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => handleError(dispatch, action, error));
  }
});

/**
 * @name createCatalogType
 * @async
 * @desc create a new catalogType
 * @param {object} catalogTypeData the new catalogType's properties
 */
export const createCatalogType = asyncAction({
  baseName: 'CREATE_CATALOG_TYPE',
  action: action => catalogTypeData => dispatch => {
    dispatch(action.start());

    return http
      .post(join(api2, '/catalogTypes'), catalogTypeData)
      .then(({ data: response }) => {
        dispatch(action.success());

        const catalogTypeId = response.id;

        return catalogTypeId;
      })
      .catch(error => handleError(dispatch, action, error));
  }
});

/**
 * @name updateCatalogType
 * @async
 * @desc update an existing catalogType
 * @param {object} params
 * @param {string} catalogTypeId the catalogTypes's id
 * @param {object} catalogTypeData the catalogTypes's new properties
 */
export const updateCatalogType = asyncAction({
  baseName: 'UPDATE_CATALOG_TYPE',
  action:
    action =>
    ({ catalogTypeId, catalogTypeData }) =>
    dispatch => {
      dispatch(action.start());

      return http
        .patch(join(api2, `/catalogTypes/${catalogTypeId}`), catalogTypeData)
        .then(() => dispatch(action.success()))
        .catch(error => handleError(dispatch, action, error));
    }
});

/**
 * @name deleteCatalogType
 * @async
 * @desc delete an existing catalogType
 * @param {string} catalogTypeId the id of the catalogTypes to be deleted
 */
export const deleteCatalogType = asyncAction({
  baseName: 'DELETE_CATALOG_TYPE',
  action: action => catalogTypeId => dispatch => {
    dispatch(action.start());

    return http
      .delete(join(api2, `/catalogTypes/${catalogTypeId}`))
      .then(() => dispatch(action.success(catalogTypeId)))
      .catch(error => handleError(dispatch, action, error));
  }
});

/**
 * @name fetchCatalogTypeCatalogs
 * @async
 * @desc fetch catalogType catalogs by ID
 * @param {string} catalogTypeId the catalogType ID
 */
export const fetchCatalogTypeCatalogs = asyncAction({
  baseName: 'FETCH_CATALOG_TYPE_CATALOGS',
  action: action => catalogTypeId => dispatch => {
    dispatch(action.start());

    return http
      .get(join(api2, `/catalogTypes/${catalogTypeId}/catalogs`))
      .then(({ data: response }) => {
        dispatch(action.success());

        return response.objects;
      })
      .catch(error => {
        handleError(dispatch, action, error);

        return [];
      });
  }
});

/**
 * @name fetchCatalogTypeDepartments
 * @async
 * @desc fetch catalogType departments by ID
 * @param {string} catalogTypeId the catalogType ID
 */
export const fetchCatalogTypeDepartments = asyncAction({
  baseName: 'FETCH_CATALOG_TYPE_DEPARTMENTS',
  action: action => catalogTypeId => dispatch => {
    dispatch(action.start());

    return http
      .get(join(api2, `/departments/?filter=catalogTypeId(${catalogTypeId})`))
      .then(({ data: response }) => {
        dispatch(action.success());

        return response.objects;
      })
      .catch(error => {
        handleError(dispatch, action, error);

        return [];
      });
  }
});

export const fetchAllCatalogTypeDepartmentAccountIds = async catalogTypeId => {
  const url = join(api2, `/departments/?count=100&filter=catalogTypeId(${catalogTypeId})`);

  const response = await http
    .get(url)
    .then(response => response.data)
    .catch(() => []);

  const totalPages = response?.pages?.totalPages;
  const totalResources = response?.pages?.totalResources;
  const departments = response?.objects;

  if (totalPages === 1) {
    return [...new Set(departments.map(d => d.accountId))];
  }

  const numberOfRequests = Math.ceil(totalResources / 100);

  const subsequentRequests = [];
  let anchor = 0;
  /* eslint-disable no-await-in-loop */
  for (let i = 0; i < numberOfRequests; i += 6) {
    const subsequestRequestsGroup = [];
    const limit = Math.min(6, numberOfRequests - i);
    for (let j = 0; j < limit; j++) {
      subsequestRequestsGroup.push(http.get(`${url}&anchor=${anchor}`));
      anchor += 100;
    }

    await Promise.all(subsequestRequestsGroup)
      .then(responses => responses.map(r => r.data.objects))
      .then(result => subsequentRequests.push(flatten(result)));
  }

  const allDepartments = flatten(subsequentRequests);
  return [...new Set(allDepartments.map(d => d.accountId))];
};
